import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { Employee, ProfileTab } from '../../types/hr';
import { displayName } from '../../utils/employees';
import { Avatar } from '../ui/Avatar';
import { PROFILE_TABS } from './ProfileHeader';
interface ProfileStickyBarProps {
  visible: boolean;
  employee: Employee;
  tab: ProfileTab;
  position: number;
  total: number;
  onTabChange: (tab: ProfileTab) => void;
  onPrev: () => void;
  onNext: () => void;
}
export function ProfileStickyBar({
  visible,
  employee,
  tab,
  position,
  total,
  onTabChange,
  onPrev,
  onNext
}: ProfileStickyBarProps) {
  const name = displayName(employee);
  return createPortal(<AnimatePresence data-id="element-521" data-mp="1n5ciqbpxl2m3">
      {visible && <motion.div initial={{
      y: -12,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: -12,
      opacity: 0
    }} transition={{
      duration: 0.18,
      ease: 'easeOut'
    }} className="fixed inset-x-0 top-16 z-10 border-b border-slate-200/70 bg-white/90 shadow-soft backdrop-blur-md lg:left-72" data-id="element-522" data-mp="1n6dygthw6da5">
          <div className="mx-auto flex h-14 max-w-[1440px] items-center gap-4 px-6 lg:px-10" data-id="element-523" data-mp="1zgpks6yb831z">
            <div className="flex min-w-0 items-center gap-3" data-id="element-524" data-mp="1vf3yjuzvycf4">
              <Avatar name={name} seed={employee.id} size="sm" data-id="element-525" data-mp="1vgov1q5rg5fm" />
              <div className="min-w-0" data-id="element-526" data-mp="2s5mp165orzbq">
                <p className="truncate text-sm font-semibold leading-tight text-navy" data-id="element-527" data-mp="2s4lcex90v4rw">{name}</p>
                <p className="hidden truncate text-xs leading-tight text-muted sm:block" data-id="element-528" data-mp="2n6tjq10jzemn">{employee.jobTitle}</p>
              </div>
            </div>

            <nav className="ml-auto flex gap-1 overflow-x-auto" role="tablist" aria-label="Profile sections" data-id="element-529" data-mp="2ee88d5z94a7u">
              {PROFILE_TABS.map(t => {
            const active = t.id === tab;
            return <button key={t.id} type="button" role="tab" aria-selected={active} onClick={() => onTabChange(t.id)} className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition ${active ? 'bg-navy text-white' : 'text-slate-600 hover:bg-canvas hover:text-navy'}`} data-id="element-530" data-mp="36434pmlj1060">
                    {t.label}
                  </button>;
          })}
            </nav>

            <div className="hidden items-center gap-1 border-l border-slate-200 pl-3 md:flex" data-id="element-531" data-mp="qgqo4ffc7qmz">
              <span className="mr-1 text-xs text-muted" data-id="element-532" data-mp="bjgb31n1qcp5">
                <span className="font-semibold text-navy" data-id="element-533" data-mp="bl18r04nqbr8">{position}</span> of {total}
              </span>
              <button type="button" onClick={onPrev} aria-label="Previous employee" className="rounded-lg p-1.5 text-navy transition hover:bg-canvas" data-id="element-534" data-mp="gjummtnuw4rx">
                <ChevronLeftIcon className="h-4 w-4" data-id="element-535" data-mp="2auzn5p3nc131" />
              </button>
              <button type="button" onClick={onNext} aria-label="Next employee" className="rounded-lg p-1.5 text-navy transition hover:bg-canvas" data-id="element-536" data-mp="3vdjur0ujfq8b">
                <ChevronRightIcon className="h-4 w-4" data-id="element-537" data-mp="3qgtg8hydt2p0" />
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>, document.body);
}